/**
 * `diagram/exportSvg.ts` — render a `DiagramModel` to a standalone SVG string
 * (interactive-diagrams-v0.1.md §7 Phase 4). Pure and dependency-free: it draws nodes + edges at
 * their `computeLayout`/sidecar positions, so the export reflects the user's **hand-tuned layout**
 * (unlike a vanilla mermaid render, which re-lays-out). Used by the NodeView's "Export SVG" action.
 *
 * Shape fidelity is approximate (the owner-accepted tradeoff, §2), but two things are faithful:
 *  - edges are trimmed to each tile's **border** (shared `edgeGeometry`), matching the on-canvas
 *    floating edges — arrows land on the shape, not at its center;
 *  - sidecar **sticky notes** and **swimlanes** (v0.4) are drawn too, so the export matches what
 *    the user sees on the canvas.
 */

import type { DiagramModel, NodeShape, StickyNote, Swimlane } from './model';
import { effectiveNodeStyle } from './model';
import { computeLayout } from './layout';
import { shapePrims, primToSvgString, INSET_SHAPES } from './react/shapes';
import { boxEdgePoint, type Box as GeomBox } from './edgeGeometry';

const NODE_H = 40;
const FONT = 13;
const PAD = 28;
const ACCENT = '#4664e6';
const STROKE = '#888';
const STICKY_FALLBACK = '#f59e0b';
const LANE_FALLBACK = '#3b82f6';
const LANE_HEADER = 26;

function estWidth(label: string): number {
  return Math.max(72, Math.round(label.length * FONT * 0.62 + 28));
}

function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface Box {
  /** Top-left corner (matches the canvas: sidecar positions are React Flow top-left). */
  x: number;
  y: number;
  w: number;
  h: number;
  shape: NodeShape;
  label: string;
  /** Native mermaid style overrides (`fill`, `stroke`, `color`, `font-size`) for this node. */
  style?: Record<string, string>;
}

/** Center of a box, as the pure edge-geometry expects. */
function geom(b: { x: number; y: number; w: number; h: number }): GeomBox {
  return { cx: b.x + b.w / 2, cy: b.y + b.h / 2, w: b.w, h: b.h };
}

/** Render the model to a standalone, self-contained SVG document string. */
export function toSvg(model: DiagramModel): string {
  const positions = computeLayout(model);
  const boxes = new Map<string, Box>();
  for (const node of model.nodes.values()) {
    const [x, y] = positions.get(node.id) ?? [0, 0];
    let w = estWidth(node.label);
    let h = NODE_H;
    if (node.shape === 'circle') {
      w = h = Math.max(w, 72);
    } else if (node.shape === 'rhombus') {
      w += 30; // diamonds need extra room — the text area is the inner square
      h += 16;
    } else if (INSET_SHAPES.has(node.shape)) {
      w += 28; // hexagon / parallelogram / trapezoid narrow toward the edges
    }
    boxes.set(node.id, {
      x,
      y,
      w,
      h,
      shape: node.shape,
      label: node.label,
      style: effectiveNodeStyle(model, node),
    });
  }

  const lanes = model.sidecar?.lanes ?? [];
  const notes = model.sidecar?.notes ?? [];

  // ── extents (nodes + annotations) ──────────────────────────────────────────
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const extend = (x: number, y: number, w: number, h: number) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  };
  for (const b of boxes.values()) extend(b.x, b.y, b.w, b.h);
  for (const l of lanes) extend(l.pos[0], l.pos[1], l.size[0], l.size[1]);
  for (const n of notes) extend(n.pos[0], n.pos[1], (n.size ?? [176, 132])[0], (n.size ?? [176, 132])[1]);
  if (!Number.isFinite(minX)) {
    minX = 0;
    minY = 0;
    maxX = 100;
    maxY = 60;
  }
  const vbX = minX - PAD;
  const vbY = minY - PAD;
  const vbW = maxX - minX + PAD * 2;
  const vbH = maxY - minY + PAD * 2;

  // ── swimlanes (behind everything) ──────────────────────────────────────────
  const laneEls = lanes.map((l) => laneToSvg(l)).join('');

  // ── edges (trimmed to node borders) ────────────────────────────────────────
  const edgeEls = model.edges
    .map((e) => {
      const a = boxes.get(e.from);
      const b = boxes.get(e.to);
      if (!a || !b) return '';
      const ga = geom(a);
      const gb = geom(b);
      const sp = boxEdgePoint(ga, gb.cx, gb.cy);
      const tp = boxEdgePoint(gb, ga.cx, ga.cy);
      const dash = e.connector === 'dotted' ? ' stroke-dasharray="4 4"' : '';
      const sw = e.connector === 'thick' ? 3 : 1.5;
      const marker = e.arrow === 'open' ? '' : ' marker-end="url(#arrow)"';
      let el =
        `<line x1="${round(sp.x)}" y1="${round(sp.y)}" x2="${round(tp.x)}" y2="${round(tp.y)}" ` +
        `stroke="${STROKE}" stroke-width="${sw}"${dash}${marker}/>`;
      if (e.label) {
        const mx = (sp.x + tp.x) / 2;
        const my = (sp.y + tp.y) / 2;
        el +=
          `<text x="${round(mx)}" y="${round(my)}" font-size="11" fill="#555" text-anchor="middle" ` +
          `dominant-baseline="middle">${xmlEscape(e.label)}</text>`;
      }
      return el;
    })
    .join('');

  // ── nodes ──────────────────────────────────────────────────────────────────
  const nodeEls = [...boxes.values()]
    .map((b) => {
      const cx = b.x + b.w / 2;
      const cy = b.y + b.h / 2;
      // Native mermaid style overrides (portable colors from `style <id> …` lines).
      const fill = b.style?.fill ?? '#fff';
      const stroke = b.style?.stroke ?? ACCENT;
      const textFill = b.style?.color ?? '#1a1a1a';
      const fontSize = b.style?.['font-size'] ?? `${FONT}`;
      // Shared geometry (shapes.tsx) — same shapes the canvas draws, translated into place.
      const { body, decor } = shapePrims(b.shape, b.w, b.h);
      const shapeEl =
        `<g transform="translate(${round(b.x)} ${round(b.y)})">` +
        primToSvgString(body, fill, stroke, 1.5) +
        decor.map((d) => primToSvgString(d, 'none', stroke, 1.5)).join('') +
        `</g>`;
      const text =
        `<text x="${round(cx)}" y="${round(cy)}" font-size="${xmlEscape(String(fontSize)).replace('px', '')}" fill="${textFill}" text-anchor="middle" ` +
        `dominant-baseline="central">${xmlEscape(b.label)}</text>`;
      return shapeEl + text;
    })
    .join('');

  // ── sticky notes (on top) ──────────────────────────────────────────────────
  const noteEls = notes.map((n) => noteToSvg(n)).join('');

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" ` +
    `width="${vbW}" height="${vbH}" font-family="system-ui, sans-serif">` +
    `<defs><marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" ` +
    `orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="${STROKE}"/></marker></defs>` +
    `<rect x="${vbX}" y="${vbY}" width="${vbW}" height="${vbH}" fill="#fff"/>` +
    `${laneEls}${edgeEls}${nodeEls}${noteEls}</svg>`
  );
}

const round = (n: number): number => Math.round(n * 100) / 100;

/** A swimlane frame: tinted body, an accent header band, and its label. */
function laneToSvg(l: Swimlane): string {
  const c = l.color ?? LANE_FALLBACK;
  const [x, y] = l.pos;
  const [w, h] = l.size;
  const vertical = l.orientation === 'vertical';
  const body =
    `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" rx="6" ` +
    `fill="${c}" fill-opacity="0.06" stroke="${c}" stroke-opacity="0.42" stroke-width="1.5"/>`;
  const header = vertical
    ? `<rect x="${round(x)}" y="${round(y)}" width="${LANE_HEADER}" height="${round(h)}" fill="${c}" fill-opacity="0.22"/>`
    : `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${LANE_HEADER}" fill="${c}" fill-opacity="0.22"/>`;
  const label = vertical
    ? `<text x="${round(x + LANE_HEADER / 2)}" y="${round(y + 10)}" font-size="12" font-weight="600" fill="#1a1a1a" ` +
      `text-anchor="middle" transform="rotate(90 ${round(x + LANE_HEADER / 2)} ${round(y + 10)})">${xmlEscape(l.label)}</text>`
    : `<text x="${round(x + 10)}" y="${round(y + LANE_HEADER / 2)}" font-size="12" font-weight="600" fill="#1a1a1a" ` +
      `dominant-baseline="central">${xmlEscape(l.label)}</text>`;
  return body + header + label;
}

/** A sticky note: tinted rounded rect + wrapped-ish text (line-split only). */
function noteToSvg(n: StickyNote): string {
  const c = n.color ?? STICKY_FALLBACK;
  const [x, y] = n.pos;
  const [w, h] = n.size ?? [176, 132];
  const rect =
    `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" rx="3" ` +
    `fill="${c}" fill-opacity="0.22" stroke="${c}" stroke-opacity="0.6" stroke-width="1"/>`;
  const lines = n.text.split('\n');
  const text =
    `<text x="${round(x + 10)}" y="${round(y + 18)}" font-size="12" fill="#1a1a1a">` +
    lines
      .map((ln, i) => `<tspan x="${round(x + 10)}" dy="${i === 0 ? 0 : 15}">${xmlEscape(ln)}</tspan>`)
      .join('') +
    `</text>`;
  return rect + text;
}
